/* MARCA | 24 Sep 2026 | Static cart drawer upsell. Opens the product modal and adds the chosen variant from the drawer. */
if (!customElements.get('cart-static-upsell')) {
  class CartStaticUpsell extends HTMLElement {
    constructor() {
      super();
      this.onKeydown = this.onKeydown.bind(this);
      this.onEscape = this.onEscape.bind(this);
      this.adding = false;
    }

    connectedCallback() {
      this.disconnectListeners?.();
      const controller = new AbortController();
      this.disconnectListeners = () => controller.abort();
      const { signal } = controller;

      this.modal = this.querySelector('[data-modal]');
      this.backdrop = this.querySelector('[data-backdrop]');
      this.openButton = this.querySelector('[data-open]');

      this.openButton?.addEventListener('click', () => this.openModal(), { signal });
      this.backdrop?.addEventListener('click', () => this.closeModal(), { signal });
      this.modal?.addEventListener('click', (event) => event.stopPropagation(), { signal });
      this.querySelector('[data-close]')?.addEventListener('click', () => this.closeModal(), { signal });
      document.addEventListener('theme:cart-drawer:close', () => this.closeModal(), { signal });
      this.querySelectorAll('[data-add]').forEach((button) => {
        button.addEventListener('click', (event) => this.addToCart(event), { signal });
      });

      document.addEventListener('theme:cart:change', () => this.syncVisibility(), { signal });
    }

    disconnectedCallback() {
      this.pauseVideo();
      this.disconnectListeners?.();
      this.releaseOverlay();
      if (this.backdrop && this.backdrop.parentElement !== this) this.append(this.backdrop);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('keyup', this.onEscape, true);
    }

    /* MARCA | 25 Sep 2026 | Include modal controls after the overlay has been moved onto the page. */
    nodes(selector) {
      const found = new Set(this.querySelectorAll(selector));
      this.modal?.querySelectorAll(selector).forEach((node) => found.add(node));
      return [...found];
    }

    /* MARCA | 25 Sep 2026 | Cover the visible drawer, not the footer block that was clipping the popup. */
    placeOverlay() {
      const panel = this.overlayPanel;
      if (!panel || !this.backdrop) return;

      this.backdrop.style.top = `${panel.scrollTop}px`;
      this.backdrop.style.left = '0';
      this.backdrop.style.width = '100%';
      this.backdrop.style.height = `${panel.clientHeight}px`;
    }

    releaseOverlay() {
      this.overlayPanel?.removeEventListener('scroll', this.onPlace);
      window.removeEventListener('resize', this.onPlace);
      this.overlayPanel = null;
    }

    openModal() {
      if (!this.modal || !this.backdrop || this.classList.contains('is-open')) return;

      const panel = this.closest('.drawer__inner') || this.closest('cart-drawer');
      if (!panel) return;

      this.previouslyFocused = document.activeElement;
      this.overlayPanel = panel;
      panel.append(this.backdrop);
      this.placeOverlay();
      this.backdrop.hidden = false;
      this.modal.hidden = false;
      this.backdrop.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');
      this.openButton?.setAttribute('aria-expanded', 'true');
      this.modal.querySelector('[data-close]')?.focus();

      this.onPlace = () => this.placeOverlay();
      panel.addEventListener('scroll', this.onPlace, { passive: true });
      window.addEventListener('resize', this.onPlace);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('keyup', this.onEscape, true);
    }

    closeModal() {
      if (!this.backdrop || !this.classList.contains('is-open')) return;

      /* MARCA | 27 Oct 2026 | Stop the modal video so it does not keep playing after the popup closes. */
      this.pauseVideo();
      this.backdrop.hidden = true;
      this.modal.hidden = true;
      this.backdrop.setAttribute('aria-hidden', 'true');
      this.backdrop.style.top = '';
      this.backdrop.style.left = '';
      this.backdrop.style.width = '';
      this.backdrop.style.height = '';
      this.append(this.backdrop);
      this.classList.remove('is-open');
      this.openButton?.setAttribute('aria-expanded', 'false');
      this.releaseOverlay();
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('keyup', this.onEscape, true);

      if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
        this.previouslyFocused.focus();
      }
    }

    /* MARCA | 24 Sep 2026 | Escape closes the product modal and leaves the cart drawer open. */
    onEscape(event) {
      if (event.code !== 'Escape' || !this.backdrop || this.backdrop.hidden) return;
      event.preventDefault();
      event.stopPropagation();
      this.closeModal();
    }

    onKeydown(event) {
      if (event.key !== 'Tab' || !this.backdrop || this.backdrop.hidden) return;

      const focusable = [...this.modal.querySelectorAll('button, [href], input, select, textarea, video')].filter((element) => {
        return !element.disabled && element.offsetParent !== null;
      });

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* MARCA | 27 Oct 2026 | Leave fullscreen and pause so the player does not keep running after the popup closes. */
    pauseVideo() {
      const video = this.modal?.querySelector('video');
      if (!video) return;

      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
      if (fullscreenElement === video) {
        (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
      }
      if (video.webkitDisplayingFullscreen) video.webkitExitFullscreen?.();
      video.pause();
    }

    /* MARCA | 24 Sep 2026 | Hide the upsell after the cart changes when this product is already in the cart. */
    async syncVisibility() {
      if (this.dataset.hideIfInCart !== 'true') return;

      const cartUrl = window.theme?.routes?.cart_url;
      if (!cartUrl) return;

      try {
        const response = await fetch(`${cartUrl}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;

        const cart = await response.json();
        const productId = Number(this.dataset.productId);
        const inCart = (cart.items || []).some((item) => Number(item.product_id) === productId);
        this.hidden = inCart;
        if (inCart) this.closeModal();
        else this.clearErrors();
      } catch (error) {
        return error;
      }
    }

    async addToCart(event) {
      const button = event.currentTarget;
      if (this.adding || button.disabled || button.hasAttribute('data-unavailable')) return;

      const variantId = Number(this.dataset.variantId);
      if (!variantId) return;

      this.adding = true;
      this.clearErrors();
      this.setAdding(true);

      try {
        const response = await fetch(window.theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
          }),
        });

        const data = await response.json();

        if (!response.ok || data.status) {
          const message = data.description && data.description !== data.message
            ? `${data.message}: ${data.description}`
            : data.message || data.description || 'Could not add this product to the cart.';
          this.showError(button, message);
          return;
        }

        if (window.theme.settings?.cartType === 'page') {
          window.location = window.theme.routes.cart_url;
          return;
        }

        document.dispatchEvent(
          new CustomEvent('theme:product:add', {
            bubbles: true,
            detail: {
              response: data,
              button,
            },
          })
        );
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
        this.closeModal();

        if (this.dataset.hideIfInCart === 'true') this.hidden = true;
      } catch (error) {
        this.showError(button, 'Could not add this product to the cart.');
      } finally {
        this.adding = false;
        this.setAdding(false);
      }
    }

    setAdding(isAdding) {
      this.nodes('[data-add]').forEach((button) => {
        button.classList.toggle('is-loading', isAdding);
        button.setAttribute('aria-busy', isAdding ? 'true' : 'false');
        button.disabled = isAdding || button.hasAttribute('data-unavailable');
      });
    }

    showError(button, message) {
      const panel = button.closest('[data-panel], [data-modal]');
      const error = panel?.querySelector('[data-error]');
      if (!error) return;
      error.hidden = false;
      error.textContent = message;
    }

    clearErrors() {
      this.nodes('[data-error]').forEach((error) => {
        error.hidden = true;
        error.textContent = '';
      });
    }
  }

  customElements.define('cart-static-upsell', CartStaticUpsell);
}
